'use client';

import { memo, PropsWithChildren, useCallback, useMemo } from 'react';
import { useIntakeWizardContext } from '../../contexts/WizardProvider';
import { useWizardNavigation } from '../../hooks/useWizardNavigation';
import { WizardMobileBackButton } from '../WizardMobileBackButton';
import { IntakeStepWrapper } from '../common/IntakeStepWrapper';

const Wrapper = ({ children }: PropsWithChildren) => (
  <div className='relative flex min-h-screen w-full flex-col bg-cover bg-center bg-no-repeat'>
    <div className='relative z-10'>
      <WizardMobileBackButton />
    </div>

    <div className='relative z-10 flex flex-1 items-start pt-8 md:items-center md:pt-0'>
      <div className='w-full'>{children}</div>
    </div>
  </div>
);

const BackgroundWrapper: React.FC<PropsWithChildren> = memo(Wrapper);

const formatList = (items: string[]): string => {
  if (items.length <= 1) return items[0] ?? '';

  if (items.length === 2) return `${items[0]} and ${items[1]}`;

  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
};

export const LocationCheckStep = () => {
  const { state } = useIntakeWizardContext();
  const { goToNextStep, goToPreviousStep, hasPreviousStep } =
    useWizardNavigation();

  const {
    nearest_location: location,
    location_has_pediatrics: hasPediatrics = false,
  } = state.formData;

  const handleSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();

      goToNextStep();
    },
    [goToNextStep]
  );

  const formattedList = useMemo(() => {
    if (!location) return undefined;

    const locations = [location.locationName, ...location.alternativeLocations];

    return formatList(locations);
  }, [location]);

  if (!location) {
    return (
      <BackgroundWrapper>
        <IntakeStepWrapper
          heading='Sollis has centers in New York, Northern California, Southern California, and South Florida.'
          description="With all-center access plus 24/7 virtual care, we're at the ready whenever you need us."
          onSubmit={handleSubmit}
          onBack={goToPreviousStep}
          showBack={hasPreviousStep}
        />
      </BackgroundWrapper>
    );
  }

  return (
    <BackgroundWrapper>
      <IntakeStepWrapper
        heading={`Sollis has centers in ${formattedList}.`}
        description='You also have unlimited access to all centers and 24/7 virtual care.'
        onSubmit={handleSubmit}
        onBack={goToPreviousStep}
        showBack={hasPreviousStep}
      >
        {hasPediatrics && (
          <p className='text-center font-sans text-[18px] leading-[130%] font-normal xl:text-[21px]'>
            Have kids? We offer all-in-one primary, urgent, and emergency
            pediatrics with an on-site Concierge Pediatrician.
          </p>
        )}
      </IntakeStepWrapper>
    </BackgroundWrapper>
  );
};
